/**
 * Comprehensive metadata interface for media items
 */
import { MediaPlacement } from '@prisma/client';
import { IMediaMetadata, MediaDimensions } from './core';

/**
 * Creates dimensions object with aspect ratio
 */
export function createDimensions(width: number, height: number, dpi?: number): MediaDimensions {
  return {
    width,
    height,
    aspectRatio: width / height,
    dpi
  };
}

/**
 * Store metadata for media items
 */
export interface StoreMediaMetadata extends IMediaMetadata {
  placement: MediaPlacement;
  mimeType: string;
  size: number;
  alt: string | null;
  caption: string | null;
  originalName: string | null;
  orderIndex: number | null;
  dimensions?: MediaDimensions;
  thumbnailUrl?: string;
  duration?: number;
  description?: string;
  customPath?: string;
}

/**
 * Metadata for media items during upload/preview
 */
export interface PreviewMediaMetadata extends StoreMediaMetadata {
  uploadStartedAt: string;
  processingStatus?: 'pending' | 'processing' | 'complete' | 'error';
}

/**
 * Creates store metadata with required fields
 */
export function createStoreMetadata(
  ownerId: string,
  options: {
    placement: MediaPlacement;
    mimeType: string;
    size: number;
    alt?: string;
    dimensions?: { width: number; height: number; dpi?: number };
    originalName?: string;
    thumbnailUrl?: string;
    duration?: number;
    description?: string;
    customPath?: string;
    orderIndex?: number;
    caption?: string;
  }
): StoreMediaMetadata {
  return {
    ownerId,
    placement: options.placement,
    mimeType: options.mimeType,
    size: options.size,
    alt: options.alt || null,
    caption: options.caption || null,
    originalName: options.originalName || null,
    orderIndex: options.orderIndex || null,
    dimensions: options.dimensions ? createDimensions(
      options.dimensions.width,
      options.dimensions.height,
      options.dimensions.dpi
    ) : undefined,
    thumbnailUrl: options.thumbnailUrl,
    duration: options.duration,
    description: options.description,
    customPath: options.customPath
  };
}

/**
 * Creates preview metadata with required fields
 */
export function createPreviewMetadata(
  ownerId: string,
  options: {
    placement: MediaPlacement;
    mimeType: string;
    size: number;
    alt?: string;
    dimensions?: { width: number; height: number; dpi?: number };
    originalName?: string;
  }
): PreviewMediaMetadata {
  return {
    ...createStoreMetadata(ownerId, {
      ...options,
      placement: options.placement
    }),
    uploadStartedAt: new Date().toISOString(),
    processingStatus: 'pending'
  };
}

/**
 * Type guard for StoreMediaMetadata
 */
export function isStoreMediaMetadata(value: unknown): value is StoreMediaMetadata {
  if (!value || typeof value !== 'object') return false;

  const metadata = value as Record<string, unknown>;

  // Check required fields
  if (
    typeof metadata.ownerId !== 'string' ||
    typeof metadata.placement !== 'string'
  ) {
    return false;
  }

  // Check optional fields
  if (metadata.alt !== undefined && metadata.alt !== null && typeof metadata.alt !== 'string') return false;
  if (metadata.caption !== undefined && metadata.caption !== null && typeof metadata.caption !== 'string') return false;
  if (metadata.originalName !== undefined && metadata.originalName !== null && typeof metadata.originalName !== 'string') return false;
  if (metadata.orderIndex !== undefined && metadata.orderIndex !== null && typeof metadata.orderIndex !== 'number') return false;
  if (metadata.thumbnailUrl !== undefined && typeof metadata.thumbnailUrl !== 'string') return false;
  if (metadata.duration !== undefined && typeof metadata.duration !== 'number') return false;
  if (metadata.description !== undefined && typeof metadata.description !== 'string') return false;
  if (metadata.customPath !== undefined && typeof metadata.customPath !== 'string') return false;

  // Check dimensions if they exist
  if (metadata.dimensions) {
    const dims = metadata.dimensions as Record<string, unknown>;
    if (
      typeof dims.width !== 'number' ||
      typeof dims.height !== 'number' ||
      typeof dims.aspectRatio !== 'number' ||
      (dims.dpi !== undefined && typeof dims.dpi !== 'number')
    ) {
      return false;
    }
  }

  return true;
}

/**
 * Parses unknown data into StoreMediaMetadata
 */
export function parseStoreMediaMetadata(value: unknown): StoreMediaMetadata {
  if (!isStoreMediaMetadata(value)) {
    throw new Error('Invalid media metadata format');
  }
  return value;
}

/**
 * Updates existing metadata with new values
 */
export function updateStoreMetadata(
  existing: StoreMediaMetadata,
  updates: Partial<StoreMediaMetadata>
): StoreMediaMetadata {
  return {
    ...existing,
    ...updates,
    // Ensure required fields are preserved
    ownerId: updates.ownerId || existing.ownerId,
    placement: updates.placement || existing.placement
  };
}

// Upload metadata type
export type UploadMediaMetadata = Partial<StoreMediaMetadata> & {
  ownerId: string; // Always required for uploads
  placement: MediaPlacement; // Always required for uploads
};
